/**
 * API 接口封装模块
 * 
 * 统一封装前端所有 API 调用，按功能模块分组，便于维护和调用。
 * 所有接口调用均基于 request.js 封装的 axios 实例，自动处理认证和错误。
 */
import request from "./request";

// ================ 认证相关接口 ================

/**
 * 获取当前登录用户信息
 * 
 * 用于验证登录状态，获取当前用户/采集者/管理员的详细信息。
 * 登录成功后调用此接口初始化用户会话。
 * 
 * @returns {Promise<Object>} 用户会话信息对象
 */
export function getMe() {
  return request.get("/auth/me");
}

/**
 * 退出登录
 * 
 * 清除服务端会话和浏览器 Cookie，返回登录页。
 * 
 * @returns {Promise<void>}
 */
export function logout() {
  return request.post("/auth/logout");
}

/** Change the current user or collector password. */
export function changePassword(data) {
  return request.post("/auth/password", data);
}

// ================ 用户端认证接口 ================

/**
 * 用户注册
 * 
 * 创建新用户账户，自动生成 API Key 并完成登录。
 * 
 * @param {Object} data - 注册数据
 * @param {string} data.account - 账户名
 * @param {string} data.password - 密码
 * @returns {Promise<Object>} 用户信息
 */
export function userRegister(data) {
  return request.post("/user/register", data);
}

/**
 * 用户登录
 * 
 * 用户端登录接口，验证账户密码并创建会话。
 * 
 * @param {Object} data - 登录数据
 * @param {string} data.account - 账户名
 * @param {string} data.password - 密码
 * @returns {Promise<Object>} 用户信息
 */
export function userLogin(data) {
  return request.post("/user/login", data);
}

// ================ 采集者端认证接口 ================

/**
 * 采集者注册
 * 
 * 创建新采集者账户，需要邀请码验证。
 * 
 * @param {Object} data - 注册数据
 * @param {string} data.account - 账户名
 * @param {string} data.password - 密码
 * @param {string} data.invite_code - 邀请码
 * @returns {Promise<Object>} 采集者信息
 */
export function collectorRegister(data) {
  return request.post("/collector/register", data);
}

/**
 * 采集者登录
 * 
 * 采集者端登录接口，验证账户密码并创建会话。
 * 
 * @param {Object} data - 登录数据
 * @param {string} data.account - 账户名
 * @param {string} data.password - 密码
 * @returns {Promise<Object>} 采集者信息
 */
export function collectorLogin(data) {
  return request.post("/collector/login", data);
}

// ================ 管理员端认证接口 ================

/**
 * 管理员登录
 * 
 * 超级管理员登录接口，验证账户密码并创建会话。
 * 
 * @param {Object} data - 登录数据
 * @param {string} data.account - 账户名
 * @param {string} data.password - 密码
 * @returns {Promise<Object>} 管理员信息
 */
export function adminLogin(data) {
  return request.post("/admin/login", data);
}

// ================ 管理员端：邀请码管理 ================

/**
 * 生成采集者邀请码
 * 
 * 创建新的一次性邀请码，有效期3小时。
 * 
 * @returns {Promise<Object>} 包含新邀请码的对象
 */
export function createCollectorInvite() {
  return request.post("/admin/collector-invites");
}

/**
 * 获取邀请码列表
 * 
 * 查询所有邀请码及其使用状态。
 * 
 * @returns {Promise<Object>} 邀请码列表
 */
export function listCollectorInvites() {
  return request.get("/admin/collector-invites");
}

// ================ 管理员端：积分设置 ================

/**
 * 获取积分单价
 * 
 * 获取系统默认的每任务积分单价。
 * 
 * @returns {Promise<Object>} 包含单价的对象
 */
export function getCreditPrice() {
  return request.get("/admin/credit-price");
}

/**
 * 设置积分单价
 * 
 * 更新系统默认的每任务积分单价。
 * 
 * @param {number} price - 积分单价
 * @returns {Promise<void>}
 */
export function setCreditPrice(price) {
  return request.put("/admin/credit-price", null, { params: { price } });
}

// ================ 管理员端：用户管理 ================

/**
 * 获取用户列表
 * 
 * 分页查询所有用户信息。
 * 
 * @param {Object} params - 查询参数
 * @param {number} params.limit - 每页数量
 * @param {number} params.offset - 偏移量
 * @returns {Promise<Object>} 用户列表
 */
export function listUsers(params) {
  return request.get("/admin/users", { params });
}

/**
 * 获取采集者列表
 * 
 * 分页查询所有采集者信息。
 * 
 * @param {Object} params - 查询参数
 * @param {number} params.limit - 每页数量
 * @param {number} params.offset - 偏移量
 * @returns {Promise<Object>} 采集者列表
 */
export function listCollectors(params) {
  return request.get("/admin/collectors", { params });
}

/**
 * 获取用户积分信息
 * 
 * 查询指定用户的积分余额和自定义单价。
 * 
 * @param {string} userId - 用户ID
 * @returns {Promise<Object>} 用户积分信息
 */
export function getUserCredit(userId) {
  return request.get(`/admin/users/${userId}/credit`);
}

/**
 * 为用户增加积分
 * 
 * 管理员手动为用户充值积分。
 * 
 * @param {string} userId - 用户ID
 * @param {number} amount - 增加的积分数量
 * @param {string} remark - 操作备注
 * @returns {Promise<void>}
 */
export function addUserCredit(userId, amount, remark) {
  return request.post(`/admin/users/${userId}/credit/add`, null, { params: { amount, remark } });
}

/**
 * 扣除用户积分
 * 
 * 管理员手动扣除用户积分。
 * 
 * @param {string} userId - 用户ID
 * @param {number} amount - 扣除的积分数量
 * @param {string} remark - 操作备注
 * @returns {Promise<void>}
 */
export function deductUserCredit(userId, amount, remark) {
  return request.post(`/admin/users/${userId}/credit/deduct`, null, { params: { amount, remark } });
}

/**
 * 获取用户积分记录
 * 
 * 分页查询指定用户的积分变动记录。
 * 
 * @param {string} userId - 用户ID
 * @param {Object} params - 查询参数
 * @returns {Promise<Object>} 积分记录列表
 */
export function getUserCreditRecords(userId, params) {
  return request.get(`/admin/users/${userId}/credit-records`, { params });
}

/**
 * 设置用户自定义积分单价
 * 
 * 为特定用户设置自定义积分单价，覆盖系统默认值。
 * 
 * @param {string} userId - 用户ID
 * @param {number|null} price - 自定义单价，null表示使用系统默认
 * @returns {Promise<void>}
 */
export function setUserCreditPrice(userId, price) {
  return request.put(`/admin/users/${userId}/credit-price`, null, { params: { price } });
}

/**
 * 封禁用户
 * 
 * 禁止用户登录和使用系统功能。
 * 
 * @param {string} userId - 用户ID
 * @returns {Promise<void>}
 */
export function banUser(userId) {
  return request.post(`/admin/users/${userId}/ban`);
}

/**
 * 解封用户
 * 
 * 恢复用户的登录和使用权限。
 * 
 * @param {string} userId - 用户ID
 * @returns {Promise<void>}
 */
export function unbanUser(userId) {
  return request.post(`/admin/users/${userId}/unban`);
}

// ================ 管理员端：代理积分管理 ================

export function listCreditAgents() {
  return request.get("/admin/agents");
}

export function getAgentCommissionSettings() {
  return request.get("/admin/agent-commission-settings");
}

export function updateAgentCommissionSettings(data) {
  return request.put("/admin/agent-commission-settings", data);
}

export function configureCreditAgent(userId, data) {
  return request.put(`/admin/agents/${userId}`, data);
}

export function removeCreditAgent(userId) {
  return request.delete(`/admin/agents/${userId}`);
}

export function grantCreditAgent(userId, amount, remark) {
  return request.post(`/admin/agents/${userId}/grant`, { amount, remark });
}

export function reclaimCreditAgent(userId, amount, remark) {
  return request.post(`/admin/agents/${userId}/reclaim`, { amount, remark });
}

export function backfillAgentClient(agentId, clientUserId) {
  return request.post(`/admin/agents/${agentId}/clients`, { client_user_id: clientUserId });
}

export function backfillUserReferrer(userId, inviterUserId) {
  return request.post(`/admin/users/${userId}/referrer`, { inviter_user_id: inviterUserId });
}

export function listCreditTransfers(params) {
  return request.get("/admin/credit-transfers", { params });
}

export function listAdminRechargeOrders(params) {
  return request.get("/admin/recharge-orders", { params });
}

export function getAdminRechargeOrderStatistics(params) {
  return request.get("/admin/recharge-orders/statistics", { params });
}

export function listUserActivities(params) {
  return request.get("/admin/user-activities", { params });
}

export function listUserApiLogs(params) {
  return request.get("/admin/user-api-logs", { params });
}

/**
 * 封禁采集者
 * 
 * 禁止采集者登录和领取任务。
 * 
 * @param {string} collectorId - 采集者ID
 * @returns {Promise<void>}
 */
export function banCollector(collectorId) {
  return request.post(`/admin/collectors/${collectorId}/ban`);
}

/**
 * 解封采集者
 * 
 * 恢复采集者的登录和任务领取权限。
 * 
 * @param {string} collectorId - 采集者ID
 * @returns {Promise<void>}
 */
export function unbanCollector(collectorId) {
  return request.post(`/admin/collectors/${collectorId}/unban`);
}

// ================ 用户端：积分管理 ================

/**
 * 获取我的积分信息
 * 
 * 查询当前登录用户的积分余额和统计数据。
 * 
 * @returns {Promise<Object>} 积分信息
 */
export function getMyCredit() {
  return request.get("/user/credit");
}

/**
 * 获取我的积分记录
 * 
 * 分页查询当前用户的积分变动记录。
 * 
 * @param {Object} params - 查询参数
 * @returns {Promise<Object>} 积分记录列表
 */
export function getMyCreditRecords(params) {
  return request.get("/user/credit-records", { params });
}

export function getMyDailyCheckin() {
  return request.get("/user/checkin");
}

export function claimMyDailyCheckin(token) {
  return request.post("/user/checkin", token);
}

export function getRechargeQuote(points) {
  return request.get("/user/recharge/quote", { params: { points } });
}

export function createRechargeOrder(data) {
  return request.post("/user/recharge/orders", data);
}

export function getRechargeOrders(params) {
  return request.get("/user/recharge/orders", { params });
}

export function getRechargeOrder(orderNo) {
  return request.get(`/user/recharge/orders/${encodeURIComponent(orderNo)}`);
}

// ================ 用户端：代理积分分发 ================

export function getMyAgentProfile() {
  return request.get("/user/agent/profile");
}

export function getMyReferral() {
  return request.get("/user/referral");
}

export function distributeAgentCredit(data) {
  return request.post("/user/agent/distributions", data);
}

/**
 * 获取我的积分单价
 * 
 * 查询当前用户适用的积分单价（自定义或系统默认）。
 * 
 * @returns {Promise<Object>} 积分单价信息
 */
export function getMyCreditPrice() {
  return request.get("/user/credit-price");
}

// ================ 管理员端：国家管理 ================

/**
 * 获取国家列表
 * 
 * 查询所有国家信息，支持按状态筛选。
 * 
 * @param {Object} params - 查询参数
 * @param {boolean} params.active_only - 是否只返回启用的国家
 * @returns {Promise<Object>} 国家列表
 */
export function listCountries(params) {
  return request.get("/admin/countries", { params });
}

export function getAdminDeviceStatus() {
  return request.get("/admin/device-status");
}

/**
 * 初始化默认国家
 * 
 * 创建系统预设的国家列表（如CN、US、JP等）。
 * 
 * @returns {Promise<Object>} 包含初始化数量和国家列表的对象
 */
export function initCountries() {
  return request.post("/admin/countries/init");
}

/**
 * 添加国家
 * 
 * 创建新的国家记录。
 * 
 * @param {Object} data - 国家数据
 * @param {string} data.code - 国家代码（如CN、US）
 * @param {string} data.name - 国家中文名称
 * @param {string} data.name_en - 国家英文名称
 * @param {number} [data.sort_order] - 排序值，默认0
 * @returns {Promise<Object>} 创建的国家对象
 */
export function createCountry(data) {
  return request.post("/admin/countries", null, { params: data });
}

/**
 * 更新国家信息
 * 
 * 修改指定国家的名称、排序或启用状态。
 * 
 * @param {string} code - 国家代码
 * @param {Object} data - 更新数据
 * @returns {Promise<void>}
 */
export function updateCountry(code, data) {
  return request.put(`/admin/countries/${code}`, null, { params: data });
}

/**
 * 删除国家
 * 
 * 删除指定国家记录。
 * 
 * @param {string} code - 国家代码
 * @returns {Promise<void>}
 */
export function deleteCountry(code) {
  return request.delete(`/admin/countries/${code}`);
}

// ================ 管理员端：项目管理 ================

/**
 * 获取所有项目列表
 * 
 * 分页查询所有项目信息。
 * 
 * @param {Object} params - 查询参数
 * @returns {Promise<Object>} 项目列表
 */
export function listAllProjects(params) {
  return request.get("/admin/projects", { params });
}

/**
 * 获取待审核项目列表
 * 
 * 分页查询状态为待审核的项目。
 * 
 * @param {Object} params - 查询参数
 * @returns {Promise<Object>} 待审核项目列表
 */
export function listPendingProjects(params) {
  return request.get("/admin/projects/pending-review", { params });
}

/**
 * 审核通过项目
 * 
 * 将项目状态从待审核变更为运行中。
 * 
 * @param {string} projectId - 项目ID
 * @returns {Promise<void>}
 */
export function approveProject(projectId) {
  return request.post(`/admin/projects/${projectId}/approve`);
}

/**
 * 拒绝项目
 * 
 * 将项目状态从待审核变更为已拒绝。
 * 
 * @param {string} projectId - 项目ID
 * @returns {Promise<void>}
 */
export function rejectProject(projectId) {
  return request.post(`/admin/projects/${projectId}/reject`);
}

/**
 * 暂停项目
 * 
 * 将项目状态从运行中变更为已暂停，暂停后无法领取新任务。
 * 
 * @param {string} projectId - 项目ID
 * @returns {Promise<void>}
 */
export function pauseProject(projectId) {
  return request.post(`/admin/projects/${projectId}/pause`);
}

/**
 * 恢复项目
 * 
 * 将项目状态从已暂停变更为运行中。
 * 
 * @param {string} projectId - 项目ID
 * @returns {Promise<void>}
 */
export function resumeProject(projectId) {
  return request.post(`/admin/projects/${projectId}/resume`);
}

// ================ 用户端：项目管理 ================

/**
 * 获取可用国家列表
 * 
 * 查询当前用户可用的国家列表（仅启用状态）。
 * 
 * @returns {Promise<Object>} 可用国家列表
 */
export function getAvailableCountries() {
  return request.get("/user/countries");
}

export function getUserDeviceStatus() {
  return request.get("/user/device-status");
}

/**
 * 获取我的项目列表
 * 
 * 查询当前用户创建的所有项目。
 * 
 * @returns {Promise<Object>} 项目列表
 */
export function getMyProjects() {
  return request.get("/user/projects");
}

export function getSelectionCategories() {
  return request.get("/user/selection/categories");
}

/**
 * 创建项目
 * 
 * 创建新的采集项目，需要选择国家和设置回调地址。
 * 
 * @param {Object} data - 项目数据
 * @param {string} data.name - 项目名称
 * @param {string} data.country - 国家代码
 * @param {string} [data.callback_url] - 回调URL，任务完成后通知此地址
 * @returns {Promise<Object>} 创建的项目对象
 */
export function createProject(data) {
  return request.post("/user/projects", null, { params: data });
}

/**
 * 获取项目详情
 * 
 * 查询指定项目的详细信息。
 * 
 * @param {string} projectId - 项目ID
 * @returns {Promise<Object>} 项目详情
 */
export function getMyProject(projectId) {
  return request.get(`/user/projects/${projectId}`);
}

/**
 * 更新项目信息
 * 
 * 修改项目的回调地址等信息。
 * 
 * @param {string} projectId - 项目ID
 * @param {Object} data - 更新数据
 * @returns {Promise<void>}
 */
export function updateMyProject(projectId, data) {
  return request.put(`/user/projects/${projectId}`, null, { params: data });
}

/**
 * 暂停我的项目
 * 
 * 暂停当前用户的项目。
 * 
 * @param {string} projectId - 项目ID
 * @returns {Promise<void>}
 */
export function pauseMyProject(projectId) {
  return request.post(`/user/projects/${projectId}/pause`);
}

/**
 * 恢复我的项目
 * 
 * 恢复当前用户已暂停的项目。
 * 
 * @param {string} projectId - 项目ID
 * @returns {Promise<void>}
 */
export function resumeMyProject(projectId) {
  return request.post(`/user/projects/${projectId}/resume`);
}

/**
 * 删除我的项目
 * 
 * 删除当前用户的项目；可选择同时软删除项目中的全部任务。
 * 
 * @param {string} projectId - 项目ID
 * @param {boolean} deleteAllTasks - 是否软删除项目内全部任务
 * @returns {Promise<{refunded_credit: number, deleted_task_count: number}>}
 */
export function deleteMyProject(projectId, deleteAllTasks = false) {
  return request.delete(`/user/projects/${projectId}`, {
    params: { delete_all_tasks: deleteAllTasks }
  });
}

// ================ 用户端：任务管理 ================

/**
 * 获取项目任务列表
 * 
 * 分页查询指定项目的任务列表，支持任务状态、同步状态、产品ID和日期筛选。
 * 
 * @param {string} projectId - 项目ID
 * @param {Object} params - 查询参数
 * @param {string} [params.status] - 任务状态筛选
 * @param {string} [params.sync_status] - 同步状态筛选
 * @param {string} [params.product_id] - 产品ID包含搜索
 * @param {string} [params.title] - 标题包含搜索（不区分大小写）
 * @param {string} [params.start_date] - 开始日期
 * @param {string} [params.end_date] - 结束日期
 * @param {number} [params.limit] - 每页数量
 * @param {number} [params.offset] - 偏移量
 * @returns {Promise<Object>} 任务列表和总数
 */
export function getProjectTasks(projectId, params) {
  return request.get(`/user/projects/${projectId}/tasks`, { params });
}

/**
 * 获取项目任务统计
 * 
 * 查询指定项目的任务状态统计（总计、待领取、进行中、已完成、失败）。
 * 
 * @param {string} projectId - 项目ID
 * @returns {Promise<Object>} 任务统计数据
 */
export function getProjectTaskStats(projectId) {
  return request.get(`/user/projects/${projectId}/tasks/stats`);
}

export function getMyErpProducts(params) {
  return request.get("/user/erp-products", { params });
}

export function deleteMyErpProduct(productId, provider) {
  return request.delete(`/user/erp-products/${productId}`, { params: { provider } });
}

/**
 * 批量创建任务
 * 
 * 为指定项目批量创建采集任务，创建时会自动扣减积分。
 * 
 * @param {string} projectId - 项目ID
 * @param {Array<Object>} tasksData - 任务数据列表
 * @param {string} tasksData[].product_id - 产品ID
 * @param {string} [tasksData[].url] - 产品链接
 * @returns {Promise<Object>} 创建结果，包含成功数量
 */
export function createProjectTasks(projectId, tasksData, allowDuplicate = false) {
  return request.post(`/user/projects/${projectId}/tasks?allow_duplicate=${allowDuplicate}`, tasksData);
}

/**
 * 获取任务详情
 * 
 * 查询指定任务的详细信息，包含原始采集数据。
 * 
 * @param {string} projectId - 项目ID
 * @param {string} taskId - 任务ID
 * @returns {Promise<Object>} 任务详情
 */
export function getProjectTaskDetail(projectId, taskId) {
  return request.get(`/user/projects/${projectId}/tasks/${taskId}`);
}

/**
 * 批量导出任务数据
 * 
 * 获取批量任务数据，包含解析字段和SKU图片。
 * 
 * @param {string} projectId - 项目ID
 * @param {Object} params - 查询参数
 * @returns {Promise<Object>} 任务列表
 */
export function exportProjectTasks(projectId, params) {
  return request.get(`/user/projects/${projectId}/export`, { params });
}

/**
 * 删除项目任务
 * 
 * 删除指定任务，删除后会返还相应积分。
 * 
 * @param {string} projectId - 项目ID
 * @param {string} taskId - 任务ID
 * @returns {Promise<void>}
 */
export function deleteProjectTask(projectId, taskId) {
  return request.delete(`/user/projects/${projectId}/tasks/${taskId}`);
}

export function batchDeleteProjectTasks(projectId, taskIds) {
  return request.post(`/user/projects/${projectId}/tasks/batch-delete`, taskIds);
}

export function moveProjectTasks(projectId, targetProjectId, taskIds) {
  return request.post(`/user/projects/${projectId}/tasks/move`, {
    target_project_id: targetProjectId,
    task_ids: taskIds
  });
}

// ================ 店小秘同步 ================

/**
 * 同步Cookie到系统
 * 
 * @param {string} cookieString - Cookie字符串
 * @returns {Promise<Object>}
 */
export function syncDianxiaomiCookie(cookieString) {
  return request.post('/dianxiaomi/sync-cookie', { cookie_string: cookieString });
}

/**
 * 验证店小秘账号
 * 
 * @returns {Promise<Object>} 验证结果，包含uid和account
 */
export function verifyDianxiaomiAccount() {
  return request.post('/dianxiaomi/verify-account');
}

/**
 * 获取Cookie同步状态
 * 
 * @returns {Promise<Object>}
 */
export function getDianxiaomiCookieInfo() {
  return request.get('/dianxiaomi/cookie-info');
}

/**
 * 代理同步单个任务到店小秘（后端携带Cookie请求店小秘，绕过CORS）
 */
export function proxySyncDianxiaomi(data) {
  return request.post('/dianxiaomi/proxy-sync', data);
}

/**
 * 获取可同步到店小秘的任务列表
 * 
 * @param {string} projectId - 项目ID
 * @param {Object} params - 查询参数
 * @param {string} [params.sync_status] - 同步状态筛选
 * @param {number} [params.limit] - 每页数量
 * @param {number} [params.offset] - 偏移量
 * @returns {Promise<Object>} 任务列表和总数
 */
export function getSyncTasks(projectId, params) {
  return request.get(`/user/projects/${projectId}/sync-tasks`, { params });
}

/**
 * 批量更新任务同步状态
 * 
 * @param {string} projectId - 项目ID
 * @param {Object} data - 更新数据
 * @param {Array<string>} data.task_ids - 任务ID列表
 * @param {string} data.sync_status - 同步状态
 * @returns {Promise<Object>} 更新结果
 */
export function updateSyncTasks(projectId, data) {
  return request.put(`/user/projects/${projectId}/sync-tasks/batch-update`, data);
}

// ================ 用户端：API Key 管理 ================

/**
 * 获取我的 API Key 列表
 * 
 * 查询当前用户的所有 API Key。
 * 
 * @returns {Promise<Object>} API Key 列表
 */
export function getUserApiKeys() {
  return request.get("/user/api-keys");
}

/**
 * 创建 API Key
 * 
 * 为当前用户生成新的 API Key。
 * 
 * @param {string} name - 密钥名称
 * @returns {Promise<Object>} 创建的 API Key 对象
 */
export function createUserApiKey(name) {
  return request.post("/user/api-keys", { name });
}

/**
 * 删除 API Key
 * 
 * 删除指定的 API Key。
 * 
 * @param {string} keyId - API Key ID
 * @returns {Promise<void>}
 */
export function deleteUserApiKey(keyId) {
  return request.delete(`/user/api-keys/${keyId}`);
}

// ================ 采集者端：任务操作 ================

/**
 * 获取采集者信息
 * 
 * 查询当前登录采集者的详细信息。
 * 
 * @returns {Promise<Object>} 采集者信息
 */
export function getCollectorMe() {
  return request.get("/collector/me");
}

/**
 * 获取可领取任务数量
 * 
 * 查询各国家可领取任务的数量统计。
 * 
 * @returns {Promise<Object>} 各国家可用任务数量
 */
export function getAvailableTasks() {
  return request.get("/collector/tasks/available");
}

/**
 * 领取任务
 * 
 * 按国家领取一个待采集任务。
 * 
 * @param {string} country - 国家代码
 * @param {string} [deviceId] - 本地设备固定 UUID
 * @returns {Promise<Object>} 任务信息
 */
export function claimTask(country, deviceId) {
  return request.post("/collector/tasks/claim", {
    info: country,
    device_id: deviceId || undefined
  });
}

/**
 * 提交任务结果
 * 
 * 提交采集结果，支持成功和失败两种状态。
 * 
 * @param {string} taskId - 任务ID
 * @param {Object} resultData - 采集结果数据
 * @returns {Promise<void>}
 */
export function submitTask(taskId, resultData) {
  return request.post("/collector/tasks/submit", {
    ...resultData,
    TaskToken: taskId
  });
}

/**
 * 获取采集者统计
 * 
 * 查询采集者的累计或单日统计数据。
 * 
 * @param {string} [date] - 日期 YYYY-MM-DD，不传则返回累计统计
 * @returns {Promise<Object>} 统计数据
 */
export function getCollectorStats(date) {
  return request.get("/collector/stats", { params: { date } });
}

/**
 * 获取采集记录
 * 
 * 分页查询采集者的任务采集记录。
 * 
 * @param {Object} params - 查询参数
 * @param {string} [params.country] - 国家筛选
 * @param {string} [params.date] - 日期筛选
 * @param {number} [params.limit] - 每页数量
 * @param {number} [params.offset] - 偏移量
 * @returns {Promise<Object>} 采集记录列表
 */
export function getCollectorRecords(params) {
  return request.get("/collector/records", { params });
}

// ================ 采集者端：API Key 管理 ================

/**
 * 获取采集者 API Key 列表
 * 
 * 查询当前采集者的所有 API Key。
 * 
 * @returns {Promise<Object>} API Key 列表
 */
export function getCollectorApiKeys() {
  return request.get("/collector/api-keys");
}

/**
 * 创建采集者 API Key
 * 
 * 为当前采集者生成新的 API Key。
 * 
 * @param {string} name - 密钥名称
 * @returns {Promise<Object>} 创建的 API Key 对象
 */
export function createCollectorApiKey(name) {
  return request.post("/collector/api-keys", { name });
}

/**
 * 删除采集者 API Key
 * 
 * 删除指定的 API Key。
 * 
 * @param {string} keyId - API Key ID
 * @returns {Promise<void>}
 */
export function deleteCollectorApiKey(keyId) {
  return request.delete(`/collector/api-keys/${keyId}`);
}

// ================ 用户端：回调管理 ================

/**
 * 获取项目回调日志
 * 
 * 分页查询指定项目的回调通知日志。
 * 
 * @param {string} projectId - 项目ID
 * @param {Object} params - 查询参数
 * @returns {Promise<Object>} 回调日志列表
 */
export function getProjectCallbackLogs(projectId, params) {
  return request.get(`/user/projects/${projectId}/callback-logs`, { params });
}

/**
 * 测试项目回调
 * 
 * 向指定的回调URL发送测试请求，验证回调配置是否正确。
 * 
 * @param {string} projectId - 项目ID
 * @param {string} callbackUrl - 回调URL
 * @returns {Promise<Object>} 测试结果
 */
export function testProjectCallback(projectId, callbackUrl) {
  return request.post(`/user/projects/${projectId}/callback-test`, null, { params: { callback_url: callbackUrl } });
}

/**
 * 更新项目回调地址
 * 
 * 修改项目的回调URL配置。
 * 
 * @param {string} projectId - 项目ID
 * @param {string} callbackUrl - 回调URL
 * @returns {Promise<void>}
 */
export function updateProjectCallbackUrl(projectId, callbackUrl) {
  return request.put(`/user/projects/${projectId}/callback-url`, null, { params: { callback_url: callbackUrl } });
}
